import React, { useState } from 'react';
import { Lock, Save, X } from 'lucide-react';
import { toast } from 'react-toastify';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { PasswordInput } from '../ui/PasswordInput';
import { SIFRE_MIN_UZUNLUK } from '../../shared/constants/businessRules';
import { Alert } from '../ui/Alert';
import { changePassword } from '../../services/authService';

interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [mevcutSifre, setMevcutSifre] = useState('');
  const [yeniSifre, setYeniSifre] = useState('');
  const [yeniSifreTekrar, setYeniSifreTekrar] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const resetForm = () => {
    setMevcutSifre('');
    setYeniSifre('');
    setYeniSifreTekrar('');
    setError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSave = async () => {
    setError(null);

    if (!mevcutSifre) {
      setError('Mevcut şifre zorunludur');
      return;
    }
    if (yeniSifre.length < SIFRE_MIN_UZUNLUK) {
      setError(`Yeni şifre en az ${SIFRE_MIN_UZUNLUK} karakter olmalıdır`);
      return;
    }
    if (yeniSifre !== yeniSifreTekrar) {
      setError('Yeni şifreler eşleşmiyor');
      return;
    }
    if (yeniSifre === mevcutSifre) {
      setError('Yeni şifre mevcut şifreyle aynı olamaz');
      return;
    }

    setLoading(true);
    try {
      await changePassword(mevcutSifre, yeniSifre);
      toast.success('Şifre başarıyla değiştirildi');
      resetForm();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Şifre değiştirilemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSave();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="sm"
      title={
        <div className="flex items-center gap-2">
          <Lock size={18} />
          Şifre Değiştir
        </div>
      }
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={loading} icon={<X size={18} />}>
            İptal
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            loading={loading}
            icon={!loading ? <Save size={18} /> : undefined}
          >
            Kaydet
          </Button>
        </>
      }
    >
      <form onSubmit={handleFormSubmit} className="space-y-4">
        {error && <Alert type="error" title="Hata">{error}</Alert>}

        <PasswordInput
          label="Mevcut Şifre"
          value={mevcutSifre}
          onChange={(e) => setMevcutSifre(e.target.value)}
          placeholder="Mevcut şifrenizi girin"
          required
          autoComplete="current-password"
        />

        <PasswordInput
          label="Yeni Şifre"
          value={yeniSifre}
          onChange={(e) => setYeniSifre(e.target.value)}
          placeholder={`En az ${SIFRE_MIN_UZUNLUK} karakter`}
          required
          autoComplete="new-password"
        />

        <PasswordInput
          label="Yeni Şifre (Tekrar)"
          value={yeniSifreTekrar}
          onChange={(e) => setYeniSifreTekrar(e.target.value)}
          placeholder="Yeni şifreyi doğrulayın"
          required
          autoComplete="new-password"
        />
      </form>
    </Modal>
  );
};
